import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal, Row, Spin, TreeSelect } from 'antd'
import {
  assign,
  find,
  get,
  includes,
  isEmpty,
  isNil,
  keys,
  mapValues,
  size,
} from 'lodash'
import { nanoid } from 'nanoid'
import PropTypes from 'prop-types'

import { useQueryParams } from '../../../core/hooks'

import { IngredientKeywordTable } from '../../../common/components/sections'
import { StyledVerticalSpace } from '../../../common/components/styled'
import {
  DROPDOWN_MIN_WIDTH,
  INGREDIENT_KEYWORDS_TYPE_LEVELS,
  PROJECT_SOURCES,
} from '../../../common/constants'
import {
  handleTreeSelectDropdownSearchHighlights,
  showErrorNotification,
} from '../../../common/helpers'
import {
  useIngredientKeywordsQuery,
  useIngredientsQuery,
  useLoggedInUser,
} from '../../../common/hooks'
import { flattenTreeStructure } from '../../../common/utils'

import { DICTIONARY_WORD_TYPES } from '../../constants'
import { useTransferDictionaryWordToIngredientMutation } from '../../hooks'
import { dictionaryWordPropType } from '../../prop-types'
import { getPaginationForDictionaryType } from '../../utils'

const DictionaryWordTransferToIngredientKeywords = ({
  node,
  languageOptions,
  onClose,
  refetchLastEvent,
}) => {
  const {
    settings: { pageSize: userPageSize },
  } = useLoggedInUser()

  const { searchValue, languageIds, ...rest } = useQueryParams()
  const [ingredientIdToTransferTo, setIngredientIdToTransferTo] = useState(null)

  const [checkedKeywordsGroupedByType, setCheckedKeywordsGroupedByType] =
    useState({
      synonyms: [],
      varieties: [],
      uncategorized: [],
      discarded: [],
    })

  const {
    options: ingredientOptions,
    isLoading: isFetchIngredientsLoading,
    error: fetchIngredientsError,
  } = useIngredientsQuery()

  const [
    fetchIngredientKeywordsById,
    { isLoading: isFetchKeywordsLoading, error: fetchKeywordsError, data },
  ] = useIngredientKeywordsQuery(ingredientIdToTransferTo, true)

  const nodeDictionaryType = useMemo(
    () => get(DICTIONARY_WORD_TYPES, node.type),
    [node.type],
  )

  const [transferDictionaryWordToIngredient, { isLoading: isTransferLoading }] =
    useTransferDictionaryWordToIngredientMutation(
      nodeDictionaryType,
      refetchLastEvent,
    )

  const [page, pageSize] = useMemo(
    () =>
      getPaginationForDictionaryType(rest, nodeDictionaryType, userPageSize),
    [rest, nodeDictionaryType, userPageSize],
  )

  const flattenedKeywordsGroupedByType = useMemo(
    () => mapValues(data, keywordTree => flattenTreeStructure(keywordTree)),
    [data],
  )

  const handleOnCheck = useCallback(
    (checkedKeys, type) => {
      const keyToCheck = checkedKeys[size(checkedKeys) - 1]

      const keywordToTransferTo = find(
        get(flattenedKeywordsGroupedByType, type),
        ({ key }) => key === keyToCheck,
      )

      const isAllowedToTransferToLevel = includes(
        INGREDIENT_KEYWORDS_TYPE_LEVELS,
        keywordToTransferTo?.level,
      )

      if (!isAllowedToTransferToLevel && !isNil(keyToCheck)) {
        return
      }

      setCheckedKeywordsGroupedByType(prev => {
        const stateWithCheckedKey = assign(prev, {
          [type]: isEmpty(checkedKeys) ? [] : [keyToCheck],
        })

        return mapValues(stateWithCheckedKey, (value, key) =>
          key !== type ? [] : value,
        )
      })
    },
    [flattenedKeywordsGroupedByType],
  )

  const handleOnChangeIngredientIdToTransferTo = useCallback(
    ingredientId => {
      setIngredientIdToTransferTo(ingredientId)
      return fetchIngredientKeywordsById(ingredientId)
    },
    [fetchIngredientKeywordsById],
  )

  const handleOnConfirm = useCallback(() => {
    const checkedType = find(
      keys(checkedKeywordsGroupedByType),
      key => !isEmpty(get(checkedKeywordsGroupedByType, key)),
    )

    const keywordsOfCheckedType = get(
      flattenedKeywordsGroupedByType,
      checkedType,
    )

    const keywordToTransferTo = find(
      keywordsOfCheckedType,
      ({ key }) =>
        key === get(checkedKeywordsGroupedByType, `${checkedType}[0]`),
    )

    const keywordFromDictionary = {
      id: nanoid(),
      term: node.term,
      ingredientId: ingredientIdToTransferTo,
      parentId: keywordToTransferTo.parentId || null,
      type: keywordToTransferTo.type,
      subtype: keywordToTransferTo.isSubtype
        ? keywordToTransferTo.subtype
        : null,
      source: PROJECT_SOURCES.METADATA,
      languageId: node.languageId,
      isName: false,
    }

    transferDictionaryWordToIngredient(
      node.id,
      keywordFromDictionary,
      { searchValue, languageIds },
      page,
      pageSize,
    )
  }, [
    checkedKeywordsGroupedByType,
    flattenedKeywordsGroupedByType,
    ingredientIdToTransferTo,
    languageIds,
    node.id,
    node.languageId,
    node.term,
    page,
    pageSize,
    searchValue,
    transferDictionaryWordToIngredient,
  ])

  useEffect(() => {
    if (fetchIngredientsError || fetchKeywordsError) {
      showErrorNotification({
        title: 'Dictionary words error',
        description: fetchIngredientsError || fetchKeywordsError,
      })
    }
  }, [fetchIngredientsError, fetchKeywordsError])

  return (
    <Modal
      visible
      width="100%"
      title={`Transfer dictionary word ${node.term}`}
      okText="Transfer"
      cancelText="Cancel"
      onCancel={onClose}
      onOk={handleOnConfirm}
      confirmLoading={isTransferLoading}
    >
      {isFetchIngredientsLoading || isFetchKeywordsLoading ? (
        <Spin />
      ) : (
        <StyledVerticalSpace>
          <Row>
            <TreeSelect
              allowClear
              showSearch
              dropdownRender={handleTreeSelectDropdownSearchHighlights}
              treeNodeFilterProp="title"
              treeData={ingredientOptions}
              value={ingredientIdToTransferTo}
              onChange={handleOnChangeIngredientIdToTransferTo}
              placeholder="Transfer to..."
              dropdownMatchSelectWidth={DROPDOWN_MIN_WIDTH}
            />
          </Row>
          {!isNil(ingredientIdToTransferTo) && !isEmpty(data) && (
            <Row>
              <IngredientKeywordTable
                data={data}
                onCheck={handleOnCheck}
                editable={false}
                checkable
                checkedKeywordNodesGroupedByType={checkedKeywordsGroupedByType}
                checkStrictly
                languageOptions={languageOptions}
              />
            </Row>
          )}
        </StyledVerticalSpace>
      )}
    </Modal>
  )
}

DictionaryWordTransferToIngredientKeywords.propTypes = {
  node: dictionaryWordPropType.isRequired,
  languageOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  onClose: PropTypes.func.isRequired,
  refetchLastEvent: PropTypes.func.isRequired,
}

export default DictionaryWordTransferToIngredientKeywords
